import { useNavigate } from 'react-router-dom';
import { useNarrowViewport } from '@/layouts/shellChrome';
import { Card, Chip, DataTable } from '@/components/kit';
import { useWalletAccess, type WalletVerb } from './useWallet';

/**
 * Message wallet ▸ Access (f29b) — two verbs, five roles.
 *
 * The verbs are the wallet's own policy as the board states it: manager FULL,
 * accountant READ, everybody else NONE. The server enforces it (`wallet:read` /
 * `wallet:write`) and `GET /me/wallet/access` reports what each role holds; the
 * verbs below come from that, the static ones only fill in until it answers.
 */

/** The rows the canvas draws, in its order, with its words. */
const ROWS: Array<{ roles: string[]; label: string; note: string; verb: 'FULL' | 'READ' | 'NONE' }> = [
  {
    roles: ['Clinic Manager'],
    label: 'Clinic manager',
    verb: 'FULL',
    note: 'tops up · sets auto top-up · flips the switches',
  },
  {
    roles: ['Billing', 'Accountant'],
    label: 'Accountant',
    verb: 'READ',
    note: 'reads the ledger, books the invoices as an expense',
  },
  {
    roles: ['Receptionist'],
    label: 'Receptionist',
    verb: 'NONE',
    note: 'sees the call list when messages pause — never a balance',
  },
  {
    roles: ['Doctor', 'Nurse', 'Technician'],
    label: 'Doctor · nurse · technician',
    verb: 'NONE',
    note: 'publishing a result never asks about money',
  },
];

export function WalletAccess({ query = '' }: { query?: string }) {
  const go = useNavigate();
  const narrow = useNarrowViewport();
  const { matrix } = useWalletAccess();
  const verbFor = (row: (typeof ROWS)[number]): WalletVerb => {
    if (!matrix) return row.verb;
    const rank: Record<WalletVerb, number> = { NONE: 0, READ: 1, FULL: 2 };
    const held = matrix.filter((m) => row.roles.includes(m.role)).map((m) => m.verb);
    if (!held.length) return row.verb;
    return held.reduce((a, b) => (rank[b] > rank[a] ? b : a));
  };
  return (
    <>
      {/* THE BOARD IS A TABLE — Role · Can · Access, 322/455/171 of the
          width — not a stack of cards. */}
      <DataTable
        caption="Who can spend and who can read the wallet"
        columns={[
          { key: 'role', label: 'Role', width: 23 },
          { key: 'can', label: 'Can', width: 32 },
          { key: 'verb', label: 'Access', width: 12 },
        ]}
        rows={ROWS.filter((row) =>
          !query.trim() ||
          `${row.label} ${row.note}`.toLowerCase().includes(query.trim().toLowerCase()),
        ).map((row) => {
          const verb = verbFor(row);
          return {
            id: row.label,
            cells: {
              role: <span className="t-strong">{row.label}</span>,
              can: <span className="t-sec mut">{row.note}</span>,
              verb: verb ? (
                <Chip kind={verb === 'FULL' ? 'brand' : verb === 'READ' ? 'soft' : 'outline'} small>
                  {verb}
                </Chip>
              ) : null,
            },
          };
        })}
      />

      {narrow ? null : (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', margin: '12px 0' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card kind="accent">
              <p className="t-sec brand">
                Two roles, two verbs: the manager SPENDS, the accountant BOOKS. Neither can do the
                other’s job — the same separation of duties the payout run uses.
              </p>
            </Card>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card kind="accent">
              <p className="t-sec brand">
                The clinical firewall: a doctor releasing a result must never see a balance, and a
                receptionist is never told the clinic is out of money.
              </p>
            </Card>
          </div>
        </div>
      )}

      <p className="t-sec mut mt12">
        Who holds which role is set in{' '}
        <button type="button" className="lnk w6 brand"
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
          onClick={() => go('/clinic-manager/settings/roles')}>
          Settings ▸ Roles &amp; access
        </button>
      </p>
    </>
  );
}
